import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useForm } from "react-hook-form";
import PageHeader from "../../components/common/PageHeader";
import ErrorAlert from "../../components/common/ErrorAlert";
import { useBuilding } from "../../context/BuildingContext";
import { usersApi } from "../../services/api";

type UserRole = "owner" | "admin" | "accountant" | "resident";

interface InviteFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: UserRole;
}

const UserInvite: React.FC = () => {
  const navigate = useNavigate();
  const { currentBuilding } = useBuilding();
  const [saveError, setSaveError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<InviteFormData>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      role: "resident",
    },
  });

  const onSubmit = async (data: InviteFormData) => {
    try {
      setSaveError(null);
      const created = await usersApi.create({
        ...data,
        buildingId: currentBuilding?.id,
        status: "invited",
      });

      // Navigate to the created user's detail page when an id is returned,
      // otherwise fall back to the users list.
      if (created?.id) {
        navigate(`/dashboard/users/${created.id}`);
      } else {
        navigate("/dashboard/users");
      }
    } catch {
      setSaveError("Failed to invite user. Please try again.");
    }
  };

  return (
    <div>
      {/* Breadcrumb */}
      <div className="mb-4 flex items-center gap-2 text-sm text-gray-500">
        <Link
          to="/dashboard/users"
          className="hover:text-primary-600 transition-colors"
        >
          Users
        </Link>
        <span>/</span>
        <span className="text-gray-900 font-medium">Invite User</span>
      </div>

      <PageHeader
        title="Invite User"
        description="Send an invitation for a new user to join this building."
      />

      {saveError && (
        <div className="mt-4">
          <ErrorAlert message={saveError} />
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-5 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">
              User Information
            </h2>
          </div>

          <div className="px-6 py-6 space-y-6">
            {/* First name */}
            <div>
              <label htmlFor="firstName" className="form-label">
                First name <span className="text-red-500">*</span>
              </label>
              <input
                id="firstName"
                type="text"
                className={`form-input mt-1 ${
                  errors.firstName
                    ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                    : ""
                }`}
                placeholder="Jane"
                {...register("firstName", {
                  required: "First name is required",
                })}
              />
              {errors.firstName && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.firstName.message}
                </p>
              )}
            </div>

            {/* Last name */}
            <div>
              <label htmlFor="lastName" className="form-label">
                Last name <span className="text-red-500">*</span>
              </label>
              <input
                id="lastName"
                type="text"
                className={`form-input mt-1 ${
                  errors.lastName
                    ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                    : ""
                }`}
                placeholder="Doe"
                {...register("lastName", {
                  required: "Last name is required",
                })}
              />
              {errors.lastName && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.lastName.message}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="form-label">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                type="email"
                className={`form-input mt-1 ${
                  errors.email
                    ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                    : ""
                }`}
                placeholder="email@example.com"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Enter a valid email address",
                  },
                })}
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="form-label">
                Phone
              </label>
              <input
                id="phone"
                type="tel"
                className="form-input mt-1"
                placeholder="+44 7700 900000"
                {...register("phone")}
              />
            </div>

            {/* Role */}
            <div>
              <label htmlFor="role" className="form-label">
                Role <span className="text-red-500">*</span>
              </label>
              <select
                id="role"
                className="form-input mt-1"
                {...register("role", { required: true })}
              >
                <option value="resident">Resident</option>
                <option value="accountant">Accountant</option>
                <option value="admin">Admin</option>
                <option value="owner">Owner</option>
              </select>
            </div>
          </div>

          <div className="px-4 sm:px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between rounded-b-lg">
            <Link
              to="/dashboard/users"
              className="inline-flex items-center justify-center w-full sm:w-auto gap-1.5 text-base sm:text-sm text-gray-600 hover:text-gray-900 transition-colors border border-gray-300 rounded-md px-4 py-2.5 sm:py-2 bg-white hover:bg-gray-50 min-h-[44px] sm:min-h-0"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Cancel
            </Link>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-primary w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "Sending invitation..." : "Send Invitation"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default UserInvite;
